import {useState, useCallback} from 'react';
import {SearchBar, Button, Icon} from '@/components/atoms';
import EmptyState from '@/components/molecules/EmptyState/EmptyState';
import ButtonMenu from '@/components/molecules/ButtonMenu';
import {COLOR} from '@/utils/constants/colors';
import {useRackCatalog} from '@/hooks/useRackCatalog';

const BULK_UPLOAD_OPTIONS = [
  {id: 'bulk-upload', label: 'Bulk Upload'},
  {id: 'bulk-upload-rack-images', label: 'Bulk Upload Rack Images'}
];

const RackCardImage = ({rack}) => {
  const images = rack.imageUrls || [];
  const [activeIndex, setActiveIndex] = useState(0);

  const handlePrev = useCallback(
    () => setActiveIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1)),
    [images.length]
  );

  const handleNext = useCallback(
    () => setActiveIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0)),
    [images.length]
  );

  if (!images.length) {
    return (
      <div className='rackCardImageContainer' data-testid='rack-card-no-image'>
        <Icon
          icon='landscape'
          size='large'
          isDark
          className='rackCardPlaceholderIcon'
        />
      </div>
    );
  }

  return (
    <>
      <div className='rackCardImageContainer'>
        <img
          src={images[activeIndex]}
          alt={`${rack.displayName} ${activeIndex + 1}`}
        />
      </div>
      {images.length > 1 && (
        <div className='rackCardCarouselControls'>
          <button
            type='button'
            className='carouselArrow'
            onClick={handlePrev}
            data-testid='carousel-prev'>
            <Icon icon='chevron_left' size='small' isDark />
          </button>
          {images.map((_, idx) => (
            <button
              key={idx}
              type='button'
              className={`carouselDot${idx === activeIndex ? ' active' : ''}`}
              onClick={() => setActiveIndex(idx)}
            />
          ))}
          <button
            type='button'
            className='carouselArrow'
            onClick={handleNext}
            data-testid='carousel-next'>
            <Icon icon='chevron_right' size='small' isDark />
          </button>
        </div>
      )}
    </>
  );
};

export const RackCatalogList = ({onAddNewRack, onViewMore}) => {
  const {
    rackList,
    totalRacks,
    isEmpty,
    handleSearchChange,
    pageIndex,
    pageSize,
    totalPages,
    handlePageChange,
    handlePageSizeChange,
    rowsPerPageOptions
  } = useRackCatalog();

  const handleBulkUploadChange = () => {
    // Placeholder for bulk upload actions
  };

  const handleAddNewRack = () => {
    if (onAddNewRack) onAddNewRack();
  };

  const handleViewMore = (rack) => {
    if (onViewMore) onViewMore(rack);
  };

  return (
    <div className='rackCatalogContentSection' data-testid='rack-catalog-list'>
      <div className='rackCatalogToolbar'>
        <div className='rackCatalogSearchBox'>
          <SearchBar
            placeholder='Search...'
            onSearchChange={handleSearchChange}
          />
        </div>
        <div className='rackCatalogActions'>
          <ButtonMenu
            buttonProps={{
              text: 'Bulk Upload',
              variant: COLOR.SECONDARY,
              size: 'medium',
              isDark: true,
              iconTrailing: 'expand_more'
            }}
            options={BULK_UPLOAD_OPTIONS}
            onChange={handleBulkUploadChange}
          />
          <Button
            id='add-new-rack'
            handleButton={handleAddNewRack}
            uiSchema={{
              title: 'Add New Rack',
              variant: COLOR.SECONDARY,
              size: 'medium',
              isDark: true
            }}
          />
        </div>
      </div>

      {isEmpty && (
        <EmptyState
          icon='desktop_windows'
          title='There is currently no data to display'
          description='Please add the necessary information to populate this section.'
          hideButton
        />
      )}

      {!isEmpty && (
        <>
          <div className='rackCatalogGrid' data-testid='rack-catalog-grid'>
            {rackList.map((rack) => (
              <div key={rack.id} className='rackCard' data-testid='rack-card'>
                <div className='rackCardTitle'>{rack.displayName}</div>
                <div className='rackCardInfo'>
                  <span>Points: {rack.pointsForRack ?? '-'}</span>
                  <span>|</span>
                  <span>ID: {rack.rackId}</span>
                </div>
                <RackCardImage rack={rack} />
                <button
                  type='button'
                  className='rackCardViewMore'
                  onClick={() => handleViewMore(rack)}
                  data-testid='rack-view-more'>
                  View More
                </button>
              </div>
            ))}
          </div>

          <div className='rackCatalogPagination'>
            <div className='paginationLeft'>
              <span>Rows per page</span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                data-testid='rack-catalog-page-size'>
                {rowsPerPageOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div className='paginationRight'>
              <button
                type='button'
                className='paginationBtn'
                disabled={pageIndex === 0}
                onClick={() => handlePageChange(0)}
                data-testid='rack-catalog-first-page'>
                <Icon icon='first_page' size='small' isDark />
              </button>
              <button
                type='button'
                className='paginationBtn'
                disabled={pageIndex === 0}
                onClick={() => handlePageChange(pageIndex - 1)}
                data-testid='rack-catalog-prev-page'>
                <Icon icon='chevron_left' size='small' isDark />
              </button>
              <span>Page</span>
              <span className='paginationPageInput'>{pageIndex + 1}</span>
              <span>of {totalPages}</span>
              <button
                type='button'
                className='paginationBtn'
                disabled={pageIndex >= totalPages - 1}
                onClick={() => handlePageChange(pageIndex + 1)}
                data-testid='rack-catalog-next-page'>
                <Icon icon='chevron_right' size='small' isDark />
              </button>
              <button
                type='button'
                className='paginationBtn'
                disabled={pageIndex >= totalPages - 1}
                onClick={() => handlePageChange(totalPages - 1)}
                data-testid='rack-catalog-last-page'>
                <Icon icon='last_page' size='small' isDark />
              </button>
            </div>
          </div>
          <div className='rackCatalogPaginationTotal'>
            {totalRacks} total items
          </div>
        </>
      )}
    </div>
  );
};
